export default function MultipleHeirsChallenges() {
  const scenarios = [
    {
      title: 'Different Financial Situations',
      description: 'One heir needs immediate cash for medical bills, another wants to maximize long-term value, and a third wants to keep the property.',
      solution: 'Our quick cash offers provide immediate liquidity while fair pricing satisfies all parties.'
    },
    {
      title: 'Geographic Distance',
      description: 'Heirs scattered across different states or countries make property management and decision-making extremely difficult.',
      solution: 'We handle everything remotely—no need for anyone to visit the property or coordinate schedules.'
    },
    {
      title: 'Emotional Attachments',
      description: 'Some family members have sentimental connections to the property, making objective business decisions challenging.',
      solution: 'Our respectful approach honors family history while providing practical solutions for moving forward.'
    },
    {
      title: 'Unequal Involvement',
      description: 'One heir manages the property while others remain uninvolved, creating resentment and disagreements about compensation.',
      solution: 'Quick sale eliminates ongoing management burden and provides clean, equal distribution.'
    }
  ];

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
          <div>
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900">
              Coordinating Multiple Heirs: The Hidden Challenge
            </h2>
            <p className="text-xl text-gray-600 mb-6 leading-relaxed">
              When multiple siblings or family members inherit an apartment building together, even the closest families face difficult decisions. Different priorities, financial needs, and emotional attachments create conflict.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              <strong className="text-gray-900">The statistics are sobering:</strong> Over 60% of inherited properties with multiple heirs experience significant family conflict during the sale process. Disagreements about pricing, timing, and property management strain relationships during an already emotional time.
            </p>
            <div className="bg-teal-50 border-l-4 border-teal-500 p-6 rounded-r-lg">
              <p className="text-gray-800 leading-relaxed">
                <i className="ri-information-line text-teal-600 text-xl mr-2"></i>
                <strong className="text-gray-900">Our experience:</strong> We've helped hundreds of families navigate these exact situations. Our straightforward process and fair cash offers eliminate most points of contention, allowing families to move forward together.
              </p>
            </div>
          </div>
          <div className="relative">
            <img
              src="https://public.readdy.ai/ai/img_res/b2c015787698e52b6ae0d2f30384c1ea.jpg"
              alt="multiple heirs selling inherited apartment building"
              className="w-full h-full rounded-2xl shadow-2xl object-cover"
            />
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {scenarios.map((item, index) => (
            <div key={index} className="bg-gray-50 rounded-xl p-8 hover:shadow-lg transition-all duration-300">
              <h3 className="text-2xl font-bold mb-4 text-gray-900">{item.title}</h3>
              <p className="text-gray-700 mb-6 leading-relaxed">{item.description}</p>
              <div className="pt-6 border-t border-gray-200">
                <p className="text-sm font-semibold text-teal-600 mb-2">Our Solution:</p>
                <p className="text-gray-700 leading-relaxed">{item.solution}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-10 text-white">
          <h3 className="text-3xl font-bold mb-6">The Cost of Delay and Disagreement</h3>
          <div className="grid md:grid-cols-3 gap-8">
            <div>
              <div className="text-4xl font-bold text-teal-400 mb-2">$8,500</div>
              <p className="text-white/80">Average monthly carrying costs while heirs debate</p>
            </div>
            <div>
              <div className="text-4xl font-bold text-teal-400 mb-2">14 months</div>
              <p className="text-white/80">Additional time when multiple heirs can't agree</p>
            </div>
            <div>
              <div className="text-4xl font-bold text-teal-400 mb-2">$119,000</div>
              <p className="text-white/80">Total cost of 14-month delay (carrying costs + lost opportunity)</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
